/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import isNil from 'lodash/isNil';
import { defineComponent } from 'vue';

// @ts-ignore Není to ESM.
import template from './viewer-stateful-root.html';

import { $root } from '../../../player/player';
import { mixinType } from '../../../util.type';
import ContentInfoParser from '../../content/info/content-info-parser';
import ControlButton from '../../control/control-button';
import { errorWithLevelWarning } from '../../error/error-with-level';
import LoaderRoot from '../../loader/loader-root';
import ProgressBar from '../../progress-bar/progress-bar';
import { isServerDeviceId } from '../../session/session-attrs';
import SetupRoot from '../../setup/setup-root';
import StatusInfoDialogRoot from '../../status-info-dialog/status-info-dialog-root';

/**
 * @import { ContentInfo } from '../../content/info/content-info'
 */

export default defineComponent({
  name: 'ViewerStatefulRoot',
  template,
  components: {
    LoaderRoot,
    ProgressBar,
    SetupRoot,
    StatusInfoDialogRoot
  },
  computed: {
    firstContent() {
      return isNil(this.contentInfo?.contentTimestamp);
    }
  },
  created() {
    this.contentInfo = null;

    // Naslouchání událostem.
    $root(this).controlManager.on('buttonDown', this.onButtonDown);
    $root(this).device.on('content', this.onContent);
  },
  mounted() {
    const root = $root(this);
    const viewerRootEl = document.getElementById('viewer-stateful-root');
    if (viewerRootEl === null) {
      throw new Error('missing viewer-stateful-root element');
    }
    this.viewerRootEl = viewerRootEl;
    (async () => {
      try {
        const { deviceId, serverUrl } = root.updateUrlConfig;
        await root.store.updateSession(root.session.id, {
          // TODO: #612 Nemuselo by se explicitně vkládat prázdný objekt, ale kvůli chování JS Data,
          // které maže všechny property z localstorage, kde původní hodnota byla null, pokud byly zadány explicitně s hodnotou undefined.
          ...(deviceId === undefined ? {} : { deviceId }),
          ...(serverUrl === undefined ? {} : { serverUrl })
        });
      } catch (err) {
        this.handleError(errorWithLevelWarning(/** @type {Error} */ (err)));
      }

      try {
        const rawContentSettings = await $root(this).contentSettingsStore.get();
        if (!isNil(rawContentSettings)) {
          // Naparsování celého uloženého settings.
          this.contentInfo = await ContentInfoParser.parse(
            root.logger,
            root.viewerRegistry,
            rawContentSettings,
            root.store
          );
          this.setCssOrientation(this.contentInfo);
        }
      } catch {
        // Settings není dostupné, nebo validní.
      }
      const isInstalled = this.isInstalled();

      if (isInstalled) {
        await root
          .deviceStart(this.contentInfo, viewerRootEl)
          .catch(this.handleError);
      }
      this.setupVisible = !isInstalled;
      this.ready = true;
    })();
  },
  data() {
    return mixinType(
      {
        ready: false,
        /** @type {ContentInfo | null} */
        contentInfo: null,
        orientationClass: 'orientation-0',
        setupVisible: false
      },
      /**
       * @type {{
       *   viewerRootEl: HTMLElement;
       * }}
       */
      ({})
    );
  },
  destroyed() {
    // Zrušení naslouchání událostem.
    $root(this).controlManager.off('buttonDown', this.onButtonDown);
    $root(this).device.off('content', this.onContent);
  },
  emits: {
    /**
     * @param {Error} error
     */
    // eslint-disable-next-line n/handle-callback-err
    error(error) {}
  },
  methods: {
    back() {
      // Setup spravuje tlačítko zpět samostatně.
      if (!this.setupVisible) {
        $root(this).appManager.exit();
      }
    },
    /**
     * @private
     * @param {Error} error
     */
    handleError(error) {
      this.$emit('error', error);
    },
    isInstalled() {
      return isServerDeviceId($root(this).session);
    },
    /**
     * @param {ControlButton} controlButton
     */
    onButtonDown(controlButton) {
      if (controlButton === ControlButton.Back) {
        this.back();
      }
    },
    /**
     * @param {ContentInfo} contentInfo
     */
    onContent(contentInfo) {
      this.contentInfo = contentInfo;
      this.setCssOrientation(contentInfo);
    },
    onSetupFinish() {
      if (!this.setupVisible) {
        return;
      }
      this.setupVisible = false;
      // Po dokončení instalace zařízení může dojít ke
      // spuštění zařízení a připojení ke Ki-Wi Serveru.
      $root(this).deviceStart(this.contentInfo, this.viewerRootEl);
    },
    /**
     * @param {ContentInfo} contentInfo
     */
    setCssOrientation(contentInfo) {
      const orientation = contentInfo.communicatorSettings.get(
        'player.light.screen.0.cssOrientation'
      );
      switch (orientation) {
        case '0':
          this.orientationClass = 'orientation-0';
          break;
        case '90':
          this.orientationClass = 'orientation-90';
          break;
        case '180':
          this.orientationClass = 'orientation-180';
          break;
        case '270':
          this.orientationClass = 'orientation-270';
          break;
        default:
          break;
      }
    }
  },
  props: {
    className: { type: String, require: true }
  }
});
